import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, Globe, ShoppingBag, Package, Loader2, ArrowRight, LogOut } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CustomerDashboard() {
  const { user, loading: authLoading, logout } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ordering, setOrdering] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/login"); return; }
    const token = localStorage.getItem("auth_token");
    fetch(`${API}/customer/dashboard`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (!r.ok) throw new Error("Not authorized"); return r.json(); })
      .then(d => setData(d))
      .catch(() => toast.error("Unable to load dashboard."))
      .finally(() => setLoading(false));
  }, [user, authLoading, navigate]);

  // Handle Stripe redirect
  useEffect(() => {
    if (searchParams.get("success") === "true") {
      const addons = searchParams.get("addons");
      if (addons) {
        const token = localStorage.getItem("auth_token");
        const addonIds = addons.split(",");
        Promise.all(addonIds.map(aid =>
          fetch(`${API}/customer/addon-paid`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify({ addon_id: aid }),
          })
        )).then(() => {
          toast.success(`${addonIds.length} add-on${addonIds.length > 1 ? "s" : ""} purchased successfully!`);
          setSelected(new Set());
          fetch(`${API}/customer/dashboard`, { headers: { Authorization: `Bearer ${token}` } })
            .then(r => r.json()).then(d => setData(d));
        });
      }
    }
    if (searchParams.get("canceled") === "true") {
      toast.error("Payment was canceled.");
    }
  }, [searchParams]);

  const orderAddon = async (addonId) => {
    setOrdering(addonId);
    try {
      const token = localStorage.getItem("auth_token");
      const res = await fetch(`${API}/customer/order-addon`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ addon_id: addonId, origin_url: window.location.origin }),
      });
      const result = await res.json();
      if (result.url) {
        window.location.href = result.url;
      } else {
        toast.error(result.detail || "Failed to create checkout.");
      }
    } catch {
      toast.error("Failed to start checkout.");
    } finally {
      setOrdering(null);
    }
  };

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center text-[#595959]">Loading...</div>;
  if (!data) return <div className="min-h-[60vh] flex items-center justify-center text-[#595959]">Unable to load dashboard.</div>;

  return (
    <div data-testid="customer-dashboard">
      {/* Header */}
      <section className="bg-[#1C2E4A] py-16 md:py-20">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#C8A97E] font-semibold mb-2">Customer Portal</p>
              <h1 className="font-serif text-3xl sm:text-4xl font-bold text-white">{data.school.name}</h1>
              {data.school.domain && (
                <p className="mt-2 text-sm text-white/50 flex items-center gap-1.5">
                  <Globe className="w-4 h-4" /> {data.school.domain}
                </p>
              )}
              <div className="flex items-center gap-3 mt-3">
                <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${data.school.payment_status === "paid" ? "bg-green-500/20 text-green-300" : "bg-yellow-500/20 text-yellow-300"}`}>
                  {data.school.payment_status === "paid" ? "Paid" : "Payment Pending"}
                </span>
                <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${data.school.status === "complete" ? "bg-green-500/20 text-green-300" : "bg-blue-500/20 text-blue-300"}`}>
                  {data.school.status === "complete" ? "Live" : data.school.status === "in_progress" ? "Building" : "Pending"}
                </span>
              </div>
            </div>
            <button onClick={() => { logout(); navigate("/"); }} className="text-white/40 hover:text-white/80 text-sm flex items-center gap-1 transition-colors">
              <LogOut className="w-4 h-4" /> Sign Out
            </button>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-5xl mx-auto px-6 md:px-12">

          {/* My Add-ons */}
          {data.purchased_addons.length > 0 && (
            <div className="mb-12">
              <h2 className="font-serif text-xl font-semibold text-[#1C2E4A] mb-4 flex items-center gap-2">
                <Package className="w-5 h-5 text-[#C8A97E]" /> My Add-ons
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                {data.purchased_addons.map((addon, i) => (
                  <div key={i} className="bg-green-50 border border-green-200 rounded-xl p-4">
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-[#1C2E4A]">{addon.addon_name}</p>
                        <p className="text-xs text-green-700 mt-0.5">${addon.price?.toLocaleString()} — Purchased</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Available Add-ons */}
          {data.available_addons.length > 0 && (
            <div>
              <h2 className="font-serif text-xl font-semibold text-[#1C2E4A] mb-2 flex items-center gap-2">
                <ShoppingBag className="w-5 h-5 text-[#C8A97E]" /> Available Add-ons
              </h2>
              <p className="text-sm text-[#595959] mb-6">Select the features you want, then checkout all at once.</p>

              <div className="space-y-3">
                {data.available_addons.map(addon => {
                  const isSelected = selected.has(addon.id);
                  return (
                    <div
                      key={addon.id}
                      data-testid={`addon-${addon.id}`}
                      onClick={() => {
                        setSelected(prev => {
                          const next = new Set(prev);
                          if (next.has(addon.id)) next.delete(addon.id);
                          else next.add(addon.id);
                          return next;
                        });
                      }}
                      className={`rounded-xl border p-5 flex items-center gap-4 cursor-pointer transition-all ${
                        isSelected
                          ? "bg-[#C8A97E]/5 border-[#C8A97E] shadow-sm"
                          : "bg-[#FAF9F6] border-[#E5E2DC] hover:shadow-sm"
                      }`}
                    >
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${
                        isSelected ? "bg-[#C8A97E] border-[#C8A97E]" : "border-[#D1CEC8] bg-white"
                      }`}>
                        {isSelected && <CheckCircle2 className="w-3.5 h-3.5 text-white" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-[#1C2E4A]">{addon.name}</p>
                        <p className="text-xs text-[#595959] mt-1 leading-relaxed">{addon.description}</p>
                      </div>
                      <div className="shrink-0 text-right">
                        {addon.sale_price && addon.sale_price > 0 && addon.sale_price < addon.price ? (
                          <>
                            <p className="text-xs text-[#595959] line-through">${addon.price.toLocaleString()}</p>
                            <p className="font-serif font-bold text-[#1C2E4A] text-lg">${addon.sale_price.toLocaleString()}</p>
                          </>
                        ) : (
                          <p className="font-serif font-bold text-[#1C2E4A] text-lg">${addon.price.toLocaleString()}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Checkout Bar */}
              {selected.size > 0 && (
                <div className="sticky bottom-0 mt-6 bg-[#1C2E4A] rounded-xl p-5 flex items-center justify-between shadow-xl" data-testid="checkout-bar">
                  <div>
                    <p className="text-white font-medium text-sm">
                      {selected.size} add-on{selected.size > 1 ? "s" : ""} selected
                    </p>
                    <p className="text-white/50 text-xs mt-0.5">
                      Total: ${data.available_addons.filter(a => selected.has(a.id)).reduce((s, a) => s + (a.sale_price && a.sale_price > 0 && a.sale_price < a.price ? a.sale_price : a.price), 0).toLocaleString()}
                    </p>
                  </div>
                  <button
                    onClick={async () => {
                      setOrdering("multi");
                      try {
                        const token = localStorage.getItem("auth_token");
                        const res = await fetch(`${API}/customer/order-addons`, {
                          method: "POST",
                          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                          body: JSON.stringify({ addon_ids: [...selected], origin_url: window.location.origin }),
                        });
                        const result = await res.json();
                        if (result.url) {
                          window.location.href = result.url;
                        } else {
                          toast.error(result.detail || "Failed to create checkout.");
                        }
                      } catch {
                        toast.error("Failed to start checkout.");
                      } finally {
                        setOrdering(null);
                      }
                    }}
                    disabled={ordering === "multi"}
                    data-testid="checkout-selected-btn"
                    className="bg-[#C8A97E] text-white hover:bg-[#B3956A] px-8 py-3 rounded-md font-medium transition-colors flex items-center gap-2 disabled:opacity-50"
                  >
                    {ordering === "multi" ? <><Loader2 className="w-4 h-4 animate-spin" /> Processing...</> : <>Checkout <ArrowRight className="w-4 h-4" /></>}
                  </button>
                </div>
              )}
            </div>
          )}

          {data.available_addons.length === 0 && data.purchased_addons.length > 0 && (
            <div className="text-center py-8">
              <CheckCircle2 className="w-10 h-10 text-green-500 mx-auto mb-3" />
              <p className="text-[#595959]">You have all available add-ons! Contact us if you need anything custom.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
